/**
 * @fileoverview Post-Signup Flow Orchestrator
 * 
 * Sequences the notification pre-prompt and Safari extension nudge
 * after profile setup completes. Only shown once per user.
 * 
 * Steps (platform-dependent):
 * 1. Notification permission pre-prompt (native only, or dev mode)
 * 2. Safari extension nudge (iOS only, or dev mode)
 */

import { useState, useCallback, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { NotificationPermissionScreen } from './NotificationPermissionScreen';
import { SafariExtensionNudge } from './SafariExtensionNudge';
import { ValueShowcaseFlow } from './ValueShowcaseFlow';
import { WelcomeCelebration } from './WelcomeCelebration';

const POST_SIGNUP_COMPLETE_KEY = 'seyu_post_signup_complete';

type FlowStep = 'value-showcase' | 'notifications' | 'safari' | 'welcome-celebration' | 'done';

/**
 * Determines which steps to show based on platform.
 * When devMode is true, all steps are shown regardless of platform.
 */
function getFlowSteps(devMode: boolean): FlowStep[] {
  const steps: FlowStep[] = ['value-showcase'];
  
  if (devMode || Capacitor.isNativePlatform()) {
    steps.push('notifications');
  }
  
  if (devMode || Capacitor.getPlatform() === 'ios') {
    steps.push('safari');
  }
  
  steps.push('welcome-celebration');
  steps.push('done');
  return steps;
}

interface PostSignupFlowProps {
  /** Called when the entire post-signup flow is complete */
  onComplete: () => void;
  /** Whether dev mode is active (bypasses platform checks) */
  devMode?: boolean;
  /** Navigate function passed from parent (avoids useNavigate context issues) */
  onNavigate?: (path: string) => void;
}

export function PostSignupFlow({ onComplete, devMode = false, onNavigate }: PostSignupFlowProps) {
  const { user } = useAuth();
  const [steps] = useState<FlowStep[]>(() => getFlowSteps(devMode));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRequesting, setIsRequesting] = useState(false);

  const currentStep = steps[currentIndex];

  // If already completed or no steps to show, finish immediately
  useEffect(() => {
    const alreadyDone = localStorage.getItem(POST_SIGNUP_COMPLETE_KEY) === 'true';
    if (alreadyDone || currentStep === 'done') {
      markComplete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const markComplete = useCallback(() => {
    localStorage.setItem(POST_SIGNUP_COMPLETE_KEY, 'true');
    onComplete();
  }, [onComplete]);

  const goNext = useCallback(() => {
    setCurrentIndex(prev => prev + 1);
  }, []);

  const handleEnableNotifications = useCallback(async () => {
    // In dev mode on web or non-native, just advance
    if (!Capacitor.isNativePlatform()) {
      goNext();
      return;
    }

    setIsRequesting(true);
    try {
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      
      if (permStatus.receive === 'granted') {
        // Attach a one-time listener to save the token, since
        // usePushNotifications may not have its listeners set up yet
        // (it skips setup when permission isn't granted at mount time).
        const saveTokenFromRegistration = async (token: { value: string }) => {
          if (!user) return;
          const platform = Capacitor.getPlatform();
          console.log('[PostSignupFlow] Saving push token for new user:', token.value.substring(0, 20) + '...');
          // Remove this token from any other users first
          await supabase
            .from('push_tokens')
            .delete()
            .eq('token', token.value)
            .neq('user_id', user.id);
          await supabase
            .from('push_tokens')
            .upsert(
              { user_id: user.id, token: token.value, platform },
              { onConflict: 'user_id,token' }
            );
        };

        await PushNotifications.addListener('registration', saveTokenFromRegistration);
        await PushNotifications.register();
      }
    } catch (error) {
      console.error('[PostSignupFlow] Notification permission error:', error);
    } finally {
      setIsRequesting(false);
      goNext();
    }
  }, [goNext]);

  const handleSetUpSafari = useCallback(() => {
    localStorage.setItem(POST_SIGNUP_COMPLETE_KEY, 'true');
    onNavigate?.('/safari-extension');
    onComplete();
  }, [onNavigate, onComplete]);

  if (currentStep === 'value-showcase') {
    return <ValueShowcaseFlow onComplete={goNext} />;
  }

  if (currentStep === 'notifications') {
    return (
      <NotificationPermissionScreen
        onEnable={handleEnableNotifications}
        onSkip={goNext}
        isRequesting={isRequesting}
      />
    );
  }

  if (currentStep === 'safari') {
    return (
      <SafariExtensionNudge
        onSetUp={handleSetUpSafari}
        onSkip={() => {
          markComplete();
        }}
      />
    );
  }

  if (currentStep === 'welcome-celebration') {
    return <WelcomeCelebration onComplete={goNext} />;
  }

  // 'done' step - handled by useEffect
  return null;
}
